/**
 * Simulint users (Story 22, ADR-0018): the caller's own record and the
 * Users page. `GET /me` is what the SPA gates on; the `/users` routes are
 * `manage_org` (task 205): an Admin with scope subtree, for the users homed
 * at their organization and below. A user outside that subtree is 404.
 * Email preferences (Story 26): every user reads and sets their own. The
 * role-level settings and the lock are an organization's
 * (`getOrgSetting(o, 'email')`, Story 48).
 */
import { request } from './client.js';
import type {
  InviteUserBody,
  ListUsersParams,
  ListUsersResult,
  Me,
  MyEmailPreferences,
  PlatformUser,
  UpdateMyEmailPreferencesBody,
  UpdateUserBody,
  UserAuditPage,
} from './types.js';

export function getMe(idToken: string): Promise<Me> {
  return request<Me>('GET', '/me', idToken);
}

export function listUsers(idToken: string, params: ListUsersParams = {}): Promise<ListUsersResult> {
  return request<ListUsersResult>('GET', '/users', idToken, {
    query: {
      org: params.org,
      children: params.children === undefined ? undefined : String(params.children),
    },
  });
}

/** 409 `email_exists` when the address already has a record. */
export function inviteUser(body: InviteUserBody, idToken: string): Promise<PlatformUser> {
  return request<PlatformUser>('POST', '/users', idToken, { body });
}

/** Any of role, scope, enabled, home_org_id. 409 `last_admin` (naming the organizations), `self_disable` or `conflict`. */
export function updateUser(
  userId: string,
  body: UpdateUserBody,
  idToken: string,
): Promise<PlatformUser> {
  return request<PlatformUser>('PATCH', `/users/${encodeURIComponent(userId)}`, idToken, { body });
}

/** Newest first; pass `next_cursor` back for the next page. */
export function listUserAudit(
  userId: string,
  idToken: string,
  cursor?: string | null,
): Promise<UserAuditPage> {
  return request<UserAuditPage>('GET', `/users/${encodeURIComponent(userId)}/audit`, idToken, {
    query: { cursor },
  });
}

export function getMyEmailPreferences(idToken: string): Promise<MyEmailPreferences> {
  return request<MyEmailPreferences>('GET', '/me/preferences', idToken);
}

/** 409 `preferences_locked` while the lock holds the caller's role; 400 `client_out_of_scope`. */
export function updateMyEmailPreferences(
  body: UpdateMyEmailPreferencesBody,
  idToken: string,
): Promise<MyEmailPreferences> {
  return request<MyEmailPreferences>('PATCH', '/me/preferences', idToken, { body });
}
